'use client'

import { useEffect, useReducer, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark as faXmarkSolid } from '@fortawesome/pro-regular-svg-icons'
import { faXmark } from '@fortawesome/pro-light-svg-icons'
import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cn } from '../utils/cn'

type TooltipContextType = {
  state: TooltipState
  dispatch: React.Dispatch<ToooltipActions>
}

type ToooltipActions =
  | { type: 'toggleOpen'; payload: { open: boolean } }
  | { type: 'toggleHover'; payload: { isHovered: boolean } }
  | { type: 'toggleShowArrow'; payload: boolean }
  | { type: 'toggleCloseIcon'; payload: boolean }

type TooltipState = {
  open: boolean
  isHovered: boolean
  showArrow: boolean
  closeIcon: boolean
}

const TooltipContext = React.createContext({} as TooltipContextType)

const useTooltipContext = () => {
  return React.useContext(TooltipContext)
}

const tooltipReducer = (state: TooltipState, action: ToooltipActions) => {
  switch (action.type) {
    case 'toggleOpen':
      return { ...state, open: action.payload.open }
    case 'toggleHover':
      return { ...state, isHovered: action.payload.isHovered }
    case 'toggleShowArrow':
      return { ...state, showArrow: action.payload }
    case 'toggleCloseIcon':
      return { ...state, closeIcon: action.payload }
  }
}

const Tooltip = ({
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root> & {
  showArrow?: boolean
}) => {
  const [state, dispatch] = useReducer(tooltipReducer, {
    open: props.open ?? props.defaultOpen ?? false,
    isHovered: false,
    showArrow: false,
    closeIcon: false,
  })

  useEffect(() => {
    dispatch({ type: 'toggleShowArrow', payload: props.showArrow || false })
  }, [props.showArrow])

  const providerstate = { state, dispatch } // NOSONAR -- this will never be expensive

  return (
    <TooltipContext.Provider value={providerstate}>
      <TooltipPrimitive.Root open={state.open || state.isHovered} {...props}>
        {children}
      </TooltipPrimitive.Root>
    </TooltipContext.Provider>
  )
}
Tooltip.displayName = 'Tooltip'

type TooltipTriggerProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Trigger
> & {
  openOnCLick?: boolean
}

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  TooltipTriggerProps
>(({ openOnCLick = true, children, ...props }, ref) => {
  const { state, dispatch } = useTooltipContext()
  return (
    <TooltipPrimitive.Trigger
      aria-label='Tooltip'
      onClick={() => {
        if (openOnCLick) {
          dispatch({
            type: 'toggleOpen',
            payload: { open: !state.open },
          })
        }
      }}
      onMouseEnter={() =>
        dispatch({ type: 'toggleHover', payload: { isHovered: true } })
      }
      onMouseLeave={() =>
        dispatch({
          type: 'toggleHover',
          payload: {
            isHovered: state.closeIcon,
          },
        })
      }
      ref={ref}
      {...props}
    >
      {children}
    </TooltipPrimitive.Trigger>
  )
})
TooltipTrigger.displayName = 'TooltipTrigger'

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    closeIcon?: boolean
    size?: 'S' | 'M'
    arrowClasses?: string
  }
>(
  (
    {
      arrowClasses = '',
      className,
      closeIcon = false,
      children,
      side = 'top',
      size = 'M',
      sideOffset = 4,
      ...props
    },
    ref,
  ) => {
    const { state, dispatch } = useTooltipContext()
    useEffect(() => {
      dispatch({
        type: 'toggleCloseIcon',
        payload: closeIcon,
      })
    }, [closeIcon, dispatch])

    const [isCloseIconHovered, setIsCloseIconHovered] = useState(false)
    return (
      <TooltipPrimitive.Content
        className={cn(
          'font-manrope shadow-button data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade bg-primary-700 relative z-50 max-w-[177px] select-none rounded py-2 !text-white will-change-[transform,opacity] sm:max-w-[176px] md:max-w-[300px]',
          {
            'text-body-xs': size === 'M',
            'text-body-2xs': size === 'S',
            'pl-3 pr-7': closeIcon,
            'px-3': !closeIcon,
            'top-1': side === 'top',
            'left-1': side === 'left',
            'right-1': side === 'right',
            'bottom-1': side === 'bottom',
          },
          className,
        )}
        // close the tooltip when the user clicks outside of it if there is no close icon
        onPointerDownOutside={() => {
          !closeIcon &&
            dispatch({ type: 'toggleOpen', payload: { open: false } })
          dispatch({ type: 'toggleHover', payload: { isHovered: false } })
        }}
        ref={ref}
        side={side}
        sideOffset={sideOffset}
        {...props}
      >
        {Boolean(closeIcon) && (
          <FontAwesomeIcon
            className='absolute right-2 top-2 z-50 text-white'
            icon={isCloseIconHovered ? faXmarkSolid : faXmark}
            onClick={() => {
              dispatch({ type: 'toggleOpen', payload: { open: false } })
              dispatch({ type: 'toggleHover', payload: { isHovered: false } })
            }}
            onMouseEnter={() => setIsCloseIconHovered(true)}
            onMouseLeave={() => setIsCloseIconHovered(false)}
          />
        )}
        {children}
        {Boolean(state.showArrow) && (
          <div
            className={cn('absolute top-0 h-full w-full', arrowClasses, {
              'right-0': side === 'left',
              'top-0': side === 'bottom',
              'left-0': ['right', 'top', 'bottom'].includes(side),
              'bottom-0': side === 'top',
            })}
          >
            <TooltipPrimitive.Arrow className={cn('fill-primary-700')} />
          </div>
        )}
      </TooltipPrimitive.Content>
    )
  },
)
TooltipContent.displayName = 'TooltipContent'

const TooltipProvider = TooltipPrimitive.Provider
TooltipProvider.displayName = 'TooltipProvider'

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
